import React from 'react';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Radio,
    TextField,
    InputAdornment,
    Typography,
    Stack,
    Checkbox,
} from '@mui/material';
import { GroupHierarchySkeleton } from 'pages/GroupHierarchy/GroupHierarchySkeleton';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { CustomTransComponent } from '../../../components/CustomTransComponent';
import LinkIcon from '@mui/icons-material/Link';
import { useTheme } from '@mui/material/styles';
import { CustomIcon } from 'components';
import { GroupPath } from 'components/GroupPath';
import { useDebounce } from '@fiji/common/src/hooks/useDebounce';
import { useLoadGroupsScrollableList } from 'hooks';
// import { InfoListItem } from '@brightlayer-ui/react-components';

interface LoadGroupsListProps {
    selectedLoadGroupId?: any;
    onLoadGroupSelect: (loadGroupId: string, groupData: any) => void;
    selectionType?: 'radio' | 'checkbox';
    modelCategory?: string[];
    showTitle?: boolean;
    globalSearch?: string;
}

export const LoadGroupsList: React.FC<LoadGroupsListProps> = (props) => {
    const { t } = useTranslation();
    const { selectedLoadGroupId, onLoadGroupSelect, selectionType, showTitle, modelCategory, globalSearch } = props;
    const isSearchControlled = globalSearch !== undefined;
    const theme = useTheme();
    const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
    const [searchQuery, setSearchQuery] = React.useState<any>();
    const [, debouncedValue] = useDebounce(undefined, undefined, searchQuery);
    const effectiveSearch = isSearchControlled ? globalSearch : debouncedValue;

    const {
        page,
        listRecords,
        isLoading: isLoadGroupsLoading,
    } = useLoadGroupsScrollableList({
        containerRef: scrollContainerRef,
        forceListWithoutDevice: true,
        modelCategory,
        globalSearch: effectiveSearch,
        pageSize: 10,
        sort: { key: 'name', sortType: 'ASC' },
        enabled: true,
    });

    const filteredLoadGroups = listRecords ?? [];
    const isInitialLoading = isLoadGroupsLoading && page === 0;

    if (isInitialLoading) {
        return (
            <Box sx={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {showTitle && (
                    <Typography variant="subtitle1" sx={{ fontWeight: 640, mb: 1, color: '#333D43' }}>
                        <CustomTransComponent translationKey={'WIDGETS:BALANCING.SELECT_ASSET_S'} />
                    </Typography>
                )}
                <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center">
                    <GroupHierarchySkeleton row={1} />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {showTitle && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mY: 1, color: '#333D43', p: 2 }}>
                    <CustomTransComponent translationKey={'WIDGETS:BALANCING.SELECT_ASSET_S'} />
                </Typography>
            )}
            {!isSearchControlled && (
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                    value={searchQuery ?? ''}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                        },
                        m: '16px !important',
                        width: 'auto',
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                    }}
                />
            )}

            <Box
                ref={scrollContainerRef}
                sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0, backgroundColor: '#fff' }}
            >
                {filteredLoadGroups.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center', height: '100%', width: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                            <CustomTransComponent translationKey={'WIDGETS:BALANCING.EMPTY_LOAD_GROUPS'} />
                        </Typography>
                    </Box>
                ) : (
                    <List sx={{ width: '100%', p: 0 }}>
                        {filteredLoadGroups.map((lg: any) => {
                            const isSelected =
                                selectionType === 'checkbox'
                                    ? Array.isArray(selectedLoadGroupId) && selectedLoadGroupId.includes(lg.id)
                                    : selectedLoadGroupId === lg.id;

                            return (
                                <>
                                    <ListItem
                                        key={lg.id}
                                        disablePadding
                                        sx={{
                                            bgcolor: isSelected ? 'rgba(2, 123, 227, 0.05)' : 'transparent',
                                            '&:hover': {
                                                bgcolor: theme.palette.action.hover,
                                            },
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        <ListItemButton
                                            onClick={() => onLoadGroupSelect(lg.id, lg)}
                                            sx={{ py: 1.5, px: 2 }}
                                            alignItems="flex-start"
                                        >
                                            <ListItemText
                                                sx={{ overflow: 'hidden', minWidth: 0 }}
                                                primary={
                                                    <Typography
                                                        variant="body1"
                                                        fontWeight={600}
                                                        color={isSelected ? '#007bc1' : 'text.primary'}
                                                    >
                                                        {lg.name}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                                            {lg.isMultipleDevice && (
                                                                <Stack
                                                                    direction="row"
                                                                    alignItems="center"
                                                                    spacing={0.5}
                                                                >
                                                                    <LinkIcon
                                                                        sx={{
                                                                            fontSize: 16,
                                                                            color: isSelected
                                                                                ? '#007bc1'
                                                                                : 'text.secondary',
                                                                        }}
                                                                    />
                                                                    <Typography
                                                                        variant="caption"
                                                                        color={
                                                                            isSelected ? '#007bc1' : 'text.secondary'
                                                                        }
                                                                        sx={{ fontWeight: 500 }}
                                                                    >
                                                                        <CustomTransComponent translationKey="WIDGETS:BALANCING.MULTIPLE_DEVICES" />
                                                                    </Typography>
                                                                </Stack>
                                                            )}
                                                            {lg.groupPath && (
                                                                <Stack
                                                                    direction="row"
                                                                    alignItems="center"
                                                                    spacing={0.5}
                                                                    sx={{ overflow: 'hidden', minWidth: 0, flex: 1 }}
                                                                >
                                                                    {!lg.isMultipleDevice && lg.deviceTypeIcon && (
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                color: isSelected
                                                                                    ? '#007bc1'
                                                                                    : 'text.secondary',
                                                                                gap: 0.75,
                                                                                overflow: 'hidden',
                                                                                minWidth: 0,
                                                                            }}
                                                                        >
                                                                            <CustomIcon
                                                                                family={
                                                                                    lg.deviceTypeIcon?.web?.family ?? ''
                                                                                }
                                                                                iconName={
                                                                                    lg.deviceTypeIcon?.web?.name ?? ''
                                                                                }
                                                                                iconProps={{
                                                                                    sx: {
                                                                                        fontSize: 16,
                                                                                        marginTop: 1.125,
                                                                                    },
                                                                                }}
                                                                            />
                                                                            <GroupPath
                                                                                pathName={lg.groupPath}
                                                                                deviceName={
                                                                                    !lg.isMultipleDevice
                                                                                        ? lg.deviceName
                                                                                        : undefined
                                                                                }
                                                                                color={
                                                                                    isSelected
                                                                                        ? '#007bc1'
                                                                                        : theme.palette.text.secondary
                                                                                }
                                                                                width="100%"
                                                                                tooltip
                                                                            />
                                                                        </Box>
                                                                    )}
                                                                </Stack>
                                                            )}
                                                        </Stack>
                                                        {lg.description && (
                                                            <Typography
                                                                className="text-truncate max-width-256"
                                                                variant="body2"
                                                                color={
                                                                    isSelected
                                                                        ? '#007bc1'
                                                                        : theme.palette.text.secondary
                                                                }
                                                                sx={{
                                                                    fontSize: '0.75rem',
                                                                    lineHeight: 1.4,
                                                                    mt: 0.5,
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrientation: 'vertical',
                                                                    overflow: 'hidden',
                                                                }}
                                                            >
                                                                {lg.description}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                }
                                            />
                                            <ListItemIcon sx={{ minWidth: 'auto', mt: 0.5 }}>
                                                {selectionType === 'checkbox' ? (
                                                    <Checkbox
                                                        checked={isSelected}
                                                        sx={{
                                                            color: theme.palette.divider,
                                                            '&.Mui-checked': {
                                                                color: '#007bc1',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <Radio
                                                        checked={isSelected}
                                                        size="small"
                                                        sx={{
                                                            color: theme.palette.divider,
                                                            '&.Mui-checked': {
                                                                color: '#007bc1',
                                                            },
                                                        }}
                                                    />
                                                )}
                                            </ListItemIcon>
                                        </ListItemButton>
                                    </ListItem>
                                    {/* <InfoListItem
                                        onClick={() => onLoadGroupSelect(lg.id, lg)}
                                        title={
                                            <Typography
                                                variant="body1"
                                                fontWeight={600}
                                                color={isSelected ? '#007bc1' : 'text.secondary'}
                                            >
                                                {lg.name}
                                            </Typography>
                                        }
                                        subtitle={[
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                {lg.isMultipleDevice && (
                                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                                        <LinkIcon
                                                            sx={{
                                                                fontSize: 16,
                                                                color: isSelected ? '#007bc1' : 'text.secondary',
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            color={isSelected ? '#007bc1' : 'text.secondary'}
                                                            sx={{ fontWeight: 500 }}
                                                        >
                                                            <CustomTransComponent translationKey="WIDGETS:BALANCING.MULTIPLE_DEVICES" />
                                                        </Typography>
                                                    </Stack>
                                                )}
                                                {lg.groupPath && (
                                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                                        {!lg.isMultipleDevice && lg.deviceTypeIcon && (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    color: isSelected ? '#007bc1' : 'text.secondary',
                                                                    gap: 0.75,
                                                                }}
                                                            >
                                                                <CustomIcon
                                                                    family={lg.deviceTypeIcon?.web?.family ?? ''}
                                                                    iconName={lg.deviceTypeIcon?.web?.name ?? ''}
                                                                    iconProps={{
                                                                        sx: { fontSize: 16, marginTop: 1.125 },
                                                                    }}
                                                                />
                                                                <GroupPath
                                                                    pathName={lg.groupPath}
                                                                    deviceName={
                                                                        !lg.isMultipleDevice ? lg.deviceName : undefined
                                                                    }
                                                                    color={
                                                                        isSelected
                                                                            ? '#007bc1'
                                                                            : 'text.secondary'
                                                                    }
                                                                />
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                )}
                                            </Stack>,
                                        ]}
                                        info={[
                                            <Typography
                                                className="text-truncate max-width-256"
                                                variant="body2"
                                                color={isSelected ? '#007bc1' : 'text.secondary'}
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    lineHeight: 1.4,
                                                    mt: 0.5,
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrientation: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {lg.description}
                                            </Typography>,
                                        ]}
                                        hidePadding
                                        rightComponent={[
                                            <>
                                                {selectionType === 'checkbox' ? (
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={() => onLoadGroupSelect(lg.id, lg)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{
                                                            color: theme.palette.divider,
                                                            '&.Mui-checked': {
                                                                color: '#007bc1',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <Radio
                                                        checked={isSelected}
                                                        size="small"
                                                        onChange={() => onLoadGroupSelect(lg.id, lg)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{
                                                            color: theme.palette.divider,
                                                            '&.Mui-checked': {
                                                                color: '#007bc1',
                                                            },
                                                        }}
                                                    />
                                                )}
                                            </>,
                                        ]}
                                        divider="full"
                                        sx={{
                                            '& .MuiListItem-root': {
                                                height: 'auto',
                                            },
                                        }}
                                    /> */}
                                </>
                            );
                        })}
                    </List>
                )}
            </Box>
        </Box>
    );
};
